/**
 * Admin Report View - Complete report with internal notes
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import ReactMarkdown from 'react-markdown';
import { MermaidDiagram } from '../../components/features/reports/MermaidDiagram';
import { ReportCharts } from '../../components/features/reports/ReportCharts';
import { 
  ArrowLeft, 
  FileText, 
  AlertCircle, 
  Download, 
  StickyNote,
  Send
} from 'lucide-react';
import styles from './AdminReportView.module.css';
import { getApiUrl } from '../../config/environments';

interface InternalNote {
  content: string;
  created_by: string;
  created_at: string;
}

interface AdminReportData {
  assessment: any;
  report: any;
  internal_notes: InternalNote[];
}

export function AdminReportView() {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const { getToken } = useAuth();

  useEffect(() => {
    fetchReport();
  }, [assessmentId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(
        `${getApiUrl()}/reports/admin/reports/${assessmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch report');
      }

      const result = await response.json();
      console.log('📊 Admin Report Data:', {
        hasReport: !!result.report,
        hasDiagrams: !!(result.report?.diagrams),
        diagramsCount: result.report?.diagrams?.length || 0,
        diagrams: result.report?.diagrams,
        reportId: result.report?.id || result.report?._id,
        reportKeys: result.report ? Object.keys(result.report) : []
      });
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading report');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newNote.trim()) return;

    try {
      setSubmittingNote(true);

      const token = await getToken();
      if (!token) throw new Error('No auth token available');

      const response = await fetch(
        `${getApiUrl()}/reports/admin/reports/${assessmentId}/notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ content: newNote }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add note');
      }

      // Refresh report to show new note
      await fetchReport();
      setNewNote('');
    } catch (err) {
      alert('Error adding note: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!data?.report) {
      alert('No hay reporte disponible para descargar');
      return;
    }

    try {
      const token = await getToken();
      if (!token) throw new Error('No auth token available');

      // Try multiple possible ID fields (MongoDB uses _id, normalized might use id)
      const reportId = data.report.id || data.report._id || data.report.report_id;
      
      if (!reportId) {
        console.error('Report data:', data.report);
        throw new Error('No se pudo encontrar el ID del reporte');
      }

      // Use admin endpoint for PDF download
      const response = await fetch(
        `${getApiUrl()}/reports/admin/${reportId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF download error:', errorText);
        throw new Error(`Error al descargar PDF: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${assessmentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Error al descargar PDF: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <AlertCircle className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Error</h2>
          <p className={styles.errorMessage}>{error}</p>
          <button
            onClick={() => navigate('/admin')}
            className={styles.errorButton}
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const { assessment, report, internal_notes } = data || {};
  const progress = assessment?.progress || {};

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTop}>
            <div>
              <Link
                to="/admin"
                className={styles.backLink}
              >
                <ArrowLeft className={styles.backLinkIcon} />
                Volver al Dashboard
              </Link>
              <h1 className={styles.title}>
                <FileText className={styles.titleIcon} />
                Reporte: {assessment?.industry_name || progress?.industry_id || 'Sin nombre'}
              </h1>
              <p className={styles.subtitle}>
                {assessment?.user_email || 'Sin email'} • {assessment?.industry_name || progress?.industry_id || 'N/A'} • {assessment?.role_name || progress?.role_id || 'N/A'}
              </p>
            </div>
            {report && (
              <button
                onClick={handleDownloadPDF}
                className={styles.downloadButton}
              >
                <Download className={styles.downloadButtonIcon} />
                Descargar PDF
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.grid}>
          {/* Report Content - Left Side (2/3) */}
          <div>
            {/* Assessment Info Card */}
            <div className={styles.card} style={{ marginBottom: 'var(--spacing-xl)' }}>
              <h2 className={styles.cardTitle}>Información del Assessment</h2>
              <dl className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <dt className={styles.infoLabel}>Empresa</dt>
                  <dd className={styles.infoValue}>
                    {assessment?.company_name || progress?.company_name || 'N/A'}
                  </dd>
                </div>
                <div className={styles.infoItem}>
                  <dt className={styles.infoLabel}>Email</dt>
                  <dd className={styles.infoValue}>{assessment?.user_email || 'N/A'}</dd>
                </div>
                <div className={styles.infoItem}>
                  <dt className={styles.infoLabel}>Industria</dt>
                  <dd className={styles.infoValue}>{assessment?.industry_name || progress?.industry_id || 'N/A'}</dd>
                </div>
                <div className={styles.infoItem}>
                  <dt className={styles.infoLabel}>Rol</dt>
                  <dd className={styles.infoValue}>{assessment?.role_name || progress?.role_id || 'N/A'}</dd>
                </div>
                <div className={styles.infoItem}>
                  <dt className={styles.infoLabel}>Fecha</dt>
                  <dd className={styles.infoValue}>
                    {assessment?.created_at 
                      ? new Date(assessment.created_at).toLocaleDateString('es-CR') 
                      : 'N/A'}
                  </dd>
                </div>
                <div className={styles.infoItem}>
                  <dt className={styles.infoLabel}>Status del Lead</dt>
                  <dd className={styles.infoValue}>{assessment?.lead_status || 'new'}</dd>
                </div>
              </dl>
            </div>

            {/* Report Content */}
            {report ? (
              <div className={styles.card}>
                <h2 className={styles.cardTitle}>{report.title}</h2>

                {/* Data Visualization Charts - FIRST */}
                <ReportCharts
                  roiCalculation={report.roi_calculation}
                  implementationRoadmap={report.implementation_roadmap}
                  businessIntelligence={report.business_intelligence}
                />

                {/* Executive Summary */}
                {report.executive_summary && (
                  <div className={styles.reportSection}>
                    <h3 className={styles.reportSectionTitle}>Resumen Ejecutivo</h3>
                    <div className={styles.markdownContent}>
                      <ReactMarkdown>{report.executive_summary}</ReactMarkdown>
                    </div>
                  </div>
                )}

                {/* Key Findings */}
                {report.key_findings && report.key_findings.length > 0 && (
                  <div className={styles.reportSection}>
                    <h3 className={styles.reportSectionTitle}>Hallazgos Clave</h3>
                    <div className={styles.markdownContent}>
                      {report.key_findings.map((finding: string, idx: number) => (
                        <div key={idx} className={styles.findingItem}>
                          <ReactMarkdown>{finding}</ReactMarkdown>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {report.recommendations && report.recommendations.length > 0 && (
                  <div className={styles.reportSection}>
                    <h3 className={styles.reportSectionTitle}>Recomendaciones</h3>
                    <div className={styles.markdownContent}>
                      {report.recommendations.map((rec: string, idx: number) => (
                        <div key={idx} className={styles.recommendationItem}>
                          <ReactMarkdown>{rec}</ReactMarkdown>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Process Diagrams */}
                {report.diagrams && report.diagrams.length > 0 && (
                  <div className={styles.reportSection}>
                    <h3 className={styles.reportSectionTitle}>Diagramas de Flujo de Proceso</h3>
                    <div className={styles.diagramsContainer}>
                      {report.diagrams
                        .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
                        .map((diagram: any, idx: number) => (
                          <MermaidDiagram
                            key={idx}
                            diagram={{
                              title: diagram.title || '',
                              description: diagram.description || '',
                              mermaidCode: diagram.mermaid_code || diagram.mermaidCode || '',
                              processType: diagram.process_type || diagram.processType || 'flowchart',
                              order: diagram.order || 0
                            }}
                          />
                        ))}
                    </div>
                  </div>
                )}

                {/* Sections - filter out duplicates already shown above */}
                {report.sections && report.sections.length > 0 && (() => {
                  const excludedTitles = [
                    'executive summary',
                    'key findings',
                    'priority recommendations',
                    'recommendations',
                    'implementation roadmap',
                    'detailed improvement points'
                  ];
                  const filteredSections = report.sections.filter(
                    (section: any) => !excludedTitles.includes((section.title || '').toLowerCase())
                  );
                  return filteredSections.length > 0 ? (
                    <div className={styles.reportSection}>
                      {filteredSections.map((section: any, idx: number) => (
                        <div key={idx} style={{ marginBottom: 'var(--spacing-md)' }}>
                          <h3 className={styles.reportSectionTitle}>{section.title}</h3>
                          <div className={styles.markdownContent}>
                            <ReactMarkdown>{section.content || ''}</ReactMarkdown>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
            ) : (
              <div className={styles.card}>
                <div className={styles.emptyState}>
                  <FileText className={styles.emptyStateIcon} />
                  <p className={styles.emptyStateText}>No se ha generado un reporte aún</p>
                </div>
              </div>
            )}
          </div>

          {/* Internal Notes - Right Side (1/3) */}
          <div>
            <div className={`${styles.card} ${styles.notesCard}`}>
              <h2 className={styles.notesTitle}>
                <StickyNote className={styles.notesTitleIcon} />
                Notas Internas
              </h2>

              {/* Add Note Form */}
              <form onSubmit={handleAddNote} className={styles.notesForm}>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Agregar nota interna..."
                  rows={4}
                  className={styles.notesTextarea}
                />
                <button
                  type="submit"
                  disabled={submittingNote || !newNote.trim()}
                  className={styles.notesSubmitButton}
                >
                  <Send className={styles.notesSubmitButtonIcon} />
                  {submittingNote ? 'Guardando...' : 'Agregar Nota'}
                </button>
              </form>

              {/* Notes List */}
              <div className={styles.notesList}>
                {internal_notes && internal_notes.length > 0 ? (
                  internal_notes.map((note, idx) => (
                    <div key={idx} className={styles.noteItem}>
                      <p className={styles.noteContent}>{note.content}</p>
                      <p className={styles.noteDate}>
                        {new Date(note.created_at).toLocaleString('es-CR')}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className={styles.noNotes}>
                    No hay notas aún
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

